<script setup lang="ts">
import { ref, watch, computed } from 'vue';
import { useI18n } from '@/plugins/i18n';
import FilterDropdown from '@/components/Search/FilterDropdown.ce.vue';

import iconCategoriesDefault from '@/assets/img/icon-search-all-filters-categories.svg';
import iconOpenDefault from '@/assets/img/icon-search-all-filters-open.svg';
import iconCloseDefault from '@/assets/img/icon-search-all-filters-close.svg';
import { useSearchStore } from '@/components/Search/store/index.ts';
import { useSearchActions } from '@/components/Search/SearchResults/Composables/useSearchActions'
import { STANDARDS_SEARCH_TAB, COLLECTIONS_SEARCH_TAB, UPDATES_SEARCH_TAB } from '@/components/Search/constants.ts';

//store
const searchStore = useSearchStore()

// @ts-ignore
import vClickOutside from 'click-outside-vue3'

//props
const props = defineProps({
    searchType: { type: String, default: STANDARDS_SEARCH_TAB, required: false },
    useTeleport: { type: Boolean, default: true, required:  false},
    className: { type: String, default: 'accordion', required: false }, //additional class name for dropdown to customize the display of dropdown
    closeOnClickOutside: { type: Boolean, default: false, required: false }, //close dropdown on click outside
    iconOpen: { type: String, default: iconOpenDefault, required: false },
    iconClose: { type: String, default: iconCloseDefault, required: false },
    iconMain: { type: String, default: iconCategoriesDefault, required: false },
    iconOpenMain: { type: String, default: iconCategoriesDefault, required: false  },
    facetFilter: { type: Boolean, default: false, required: false  },
});

const filterPrefix = props.facetFilter ? `${props.searchType}.facetFilter` : props.searchType;


const { 
  runSearch
} = useSearchActions(
  props.searchType as typeof STANDARDS_SEARCH_TAB | typeof COLLECTIONS_SEARCH_TAB,
  []
)

//translations
const i18nInstance: any = useI18n()
const { t } = i18nInstance

//refs
const isOpen = ref(false);

//event handlers
const handleHeaderClick = () => {
    isOpen.value = !isOpen.value;
}

const handleToClickOutsideDropdown = () => {
  if(props.closeOnClickOutside) {
    isOpen.value = false;
  }
}

const handleICSCodeTyped = (event: KeyboardEvent) => {
    //clear the selected options in the dropdowns
    searchStore[`${filterPrefix}.descriptorField`] = [];
    searchStore[`${filterPrefix}.descriptorGroup`] = [];
    searchStore[`${filterPrefix}.descriptorSubGroup`] = [];
    //if enter do search
    if(props.facetFilter && event && (event as KeyboardEvent).key === 'Enter') {
        runSearch(true);;
    }
}

const handleLevel1Update = (items: any) => {
    searchStore[`${filterPrefix}.icsCode`] = '';
    searchStore[`${filterPrefix}.descriptorGroup`] = [];
    searchStore[`${filterPrefix}.descriptorSubGroup`] = [];
    searchStore[`lookUpOptions.${filterPrefix}.descriptorGroup`] = [];
    searchStore[`lookUpOptions.${filterPrefix}.descriptorSubGroup`] = [];
    searchStore.loadICSCodeOptions({ field: `lookUpOptions.${filterPrefix}.descriptorGroup`, icsCode: searchStore[`${filterPrefix}.descriptorField`], level: 2 })
    if(props.facetFilter) {
        runSearch(true);;
    }
}

const handleLevel2Update = (items: any) => {
    searchStore[`${filterPrefix}.icsCode`] = '';
    searchStore[`${filterPrefix}.descriptorSubGroup`] = [];
    searchStore[`lookUpOptions.${filterPrefix}.descriptorSubGroup`] = [];
    searchStore.loadICSCodeOptions({ field: `lookUpOptions.${filterPrefix}.descriptorSubGroup`, icsCode: searchStore[`${filterPrefix}.descriptorGroup`], level: 3 });
    if(props.facetFilter) {
       runSearch(true);;
    }
}

const handleLevel3Update = (items: any) => {
    searchStore[`${filterPrefix}.icsCode`] = '';
    if(props.facetFilter) {
        runSearch(true);;
    }
}

//computed
const getSelectedCount = computed(() => {
    let count = 0;
    count += ((searchStore[`${filterPrefix}.descriptorField`] || []).length) ? 1 : 0; 
    count += ((searchStore[`${filterPrefix}.descriptorGroup`] || []).length) ? 1 : 0;
    count += ((searchStore[`${filterPrefix}.descriptorSubGroup`] || []).length) ? 1 : 0;
    if(count === 0) { //set count only if icsCode is set and no other levels are selected
        count += ((searchStore[`${filterPrefix}.icsCode`] || '').length) ? 1 : 0;
    }

  return count ? count : '';
})

const getInitialOptionsLevel1 = computed(() => {
    const options = [];
    const fieldOptions = searchStore[`${filterPrefix}.descriptorField`] || [];
    options.push(...fieldOptions);
    return options;
});
const getInitialOptionsLevel2 = computed(() => {
    const options = [];
    const groupOptions = searchStore[`${filterPrefix}.descriptorGroup`] || [];
    options.push(...groupOptions);
    return options;
});
const getInitialOptionsLevel3 = computed(() => {
    const options = [];
    const subGroupOptions = searchStore[`${filterPrefix}.descriptorSubGroup`] || [];    
    options.push(...subGroupOptions);
    return options;
});

const disableLevel2 = computed(() => {
    const groupOptions = searchStore[`lookUpOptions.${filterPrefix}.descriptorGroup`] || [];    
    return groupOptions.length === 0;
});
const disableLevel3 = computed(() => {
    const subGroupOptions = searchStore[`lookUpOptions.${filterPrefix}.descriptorSubGroup`] || [];    
    return subGroupOptions.length === 0;
});
//directives
const vClickOutsideAction = vClickOutside.directive


</script>

<template>
    <div :class="['ics-code-dropdown', { 'open': isOpen }, className]"  v-click-outside-action="handleToClickOutsideDropdown">
        <div :class="[ 'ics-code-dropdown--header', { 'open': isOpen } ]" @click="handleHeaderClick">
            <img v-if="isOpen" class="icon-left" :src="iconOpenMain" :alt="t('categories')"></img>
            <img v-else class="icon-left" :src="iconMain"></img>
            <div class="placeholder">{{ t('categories') }}</div>
            <div class="selection--count">
                <div>{{ getSelectedCount }}</div>
            </div>
            <img v-if="isOpen" class="icon-right" :src="iconOpen"></img>
            <img v-else class="icon-right" :src="iconClose"></img>
        </div>
        <div class="ics-code-dropdown--body" v-show="isOpen">
            <div class="ics-code code">
                <input type="text" name="icsCode" :placeholder="t('icsCodePlaceHolder')" v-model="searchStore[`${filterPrefix}.icsCode`]"  @keyup="handleICSCodeTyped" autocomplete="off"/>
            </div>
            <div class="separator"><div class="separator--text">{{ t('or') }}</div></div>
            <div class="ics-code level">
                <div class="ics-code--level1">
                    <FilterDropdown
                    className="dropdown-select dropdown"
                    :placeholder="t('field')" 
                    name="icsCode-level1" 
                    :field="`${filterPrefix}.descriptorField`"
                    :lookupField="`lookUpOptions.${filterPrefix}.descriptorField`" 
                    :defaultOptionLookupField="`lookUpOptions.${props.searchType}.descriptorField`" 
                    :initialOptions="getInitialOptionsLevel1" 
                    :hasClear="false" 
                    :showMoreLink="false"
                    :closeOnClickOutside="true"
                    :showSelectCountInHeader="true"
                    :useTeleport="useTeleport"
                    :closeOnSelect="true"
                    :showSelectedInPlaceholder="true"
                    @updateSelectedItems="handleLevel1Update"
                    />
                </div>
                <div class="ics-code--level2">
                    <FilterDropdown
                    className="dropdown-select dropdown"
                    :placeholder="t('group')" 
                    name="icsCode-level2" 
                    :field="`${filterPrefix}.descriptorGroup`"
                    :lookupField="`lookUpOptions.${filterPrefix}.descriptorGroup`" 
                    :initialOptions="getInitialOptionsLevel2" 
                    :hasClear="false" 
                    :showMoreLink="false"
                    :closeOnClickOutside="true"
                    :showSelectCountInHeader="true"
                    :useTeleport="useTeleport"
                    :closeOnSelect="true"
                    :showSelectedInPlaceholder="true"
                    :disabled="disableLevel2"
                     @updateSelectedItems="handleLevel2Update"
                    />
                </div>
                <div class="ics-code--level3">
                    <FilterDropdown
                    className="dropdown-select dropdown"
                    :placeholder="t('subGroup')" 
                    name="icsCode-level3" 
                    :field="`${filterPrefix}.descriptorSubGroup`"
                    :lookupField="`lookUpOptions.${filterPrefix}.descriptorSubGroup`" 
                    :initialOptions="getInitialOptionsLevel3" 
                    :hasClear="false" 
                    :showMoreLink="false"
                    :closeOnClickOutside="true"
                    :showSelectCountInHeader="true"
                    :useTeleport="useTeleport"
                    :closeOnSelect="true"
                    :showSelectedInPlaceholder="true"
                    :disabled="disableLevel3"
                     @updateSelectedItems="handleLevel3Update"
                    />
                </div>
            </div>
        </div>
    </div>
</template>
<style lang="scss">
    @use './style.scss';
</style>